<template>
    <div class="py-10 flex flex-col items-center w-full space-y-6">

        <!-- Filters: Show (left) | Search + Subtopic (right) -->
        <div class="w-3/4 space-y-1">
            <div class="flex flex-wrap items-end justify-between gap-4">
                <!-- Left: mode -->
                <div class="flex flex-col gap-1">
                    <label class="text-xs text-warm-gray-500 dark:text-warm-gray-400">Show:</label>
                    <div class="flex items-center gap-2">
                        <select
                            v-model="mode"
                            @change="onModeChange"
                            class="explanation-mode-select rounded border border-warm-gray-400 dark:border-warm-gray-500 bg-white dark:bg-warm-gray-700 text-warm-gray-800 dark:text-warm-gray-200 pl-2 pr-8 py-1 text-xs min-w-[7.5rem] cursor-pointer disabled:opacity-50"
                            :disabled="isRetrieving || !!activeQuestionId"
                        >
                            <option value="downvoted">Downvoted</option>
                            <option value="all">All</option>
                        </select>
                        <span class="text-xs text-warm-gray-500 dark:text-warm-gray-400 whitespace-nowrap">
                            <template v-if="totalCount != null">{{ countLabel }}</template>
                            <template v-else-if="isCounting">Counting…</template>
                        </span>
                    </div>
                </div>

                <!-- Right: search + subtopic -->
                <div class="flex flex-wrap items-end gap-3">
                    <div class="flex flex-col gap-1">
                        <label class="text-xs text-warm-gray-500 dark:text-warm-gray-400">Search questionId (exact)</label>
                        <div class="flex items-center gap-2">
                            <input
                                v-model="searchQuestionIdInput"
                                type="text"
                                placeholder="e.g. aircraft-fitting_12"
                                class="rounded border border-warm-gray-400 dark:border-warm-gray-500 bg-white dark:bg-warm-gray-700 text-warm-gray-800 dark:text-warm-gray-200 px-2 py-1 text-xs w-52"
                                @keyup.enter="applySearch"
                            />
                            <button
                                type="button"
                                @click="applySearch"
                                class="rounded border border-warm-gray-600 text-warm-gray-700 dark:border-warm-gray-400 dark:text-warm-gray-300 px-2 py-1 text-xs"
                                :disabled="isRetrieving"
                            >
                                Search
                            </button>
                            <button
                                v-if="activeQuestionId"
                                type="button"
                                @click="clearSearch"
                                class="rounded border border-warm-gray-400 text-warm-gray-500 dark:border-warm-gray-500 dark:text-warm-gray-400 px-2 py-1 text-xs"
                                :disabled="isRetrieving"
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label class="text-xs text-warm-gray-500 dark:text-warm-gray-400">Filter by subtopic</label>
                        <div class="flex items-center gap-2">
                            <input
                                v-model="subtopicIdInput"
                                type="text"
                                placeholder="Enter subtopic ID e.g. aircraft-fitting"
                                class="rounded border border-warm-gray-400 dark:border-warm-gray-500 bg-white dark:bg-warm-gray-700 text-warm-gray-800 dark:text-warm-gray-200 px-2 py-1 text-xs w-56"
                                :disabled="!!activeQuestionId"
                                @keyup.enter="applySubtopicFilter"
                            />
                            <button
                                type="button"
                                @click="applySubtopicFilter"
                                class="rounded border border-warm-gray-600 text-warm-gray-700 dark:border-warm-gray-400 dark:text-warm-gray-300 px-2 py-1 text-xs"
                                :disabled="isRetrieving || !!activeQuestionId"
                            >
                                Apply
                            </button>
                            <button
                                v-if="activeSubtopicId"
                                type="button"
                                @click="clearSubtopicFilter"
                                class="rounded border border-warm-gray-400 text-warm-gray-500 dark:border-warm-gray-500 dark:text-warm-gray-400 px-2 py-1 text-xs"
                                :disabled="isRetrieving || !!activeQuestionId"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="activeQuestionId" class="text-xs text-warm-gray-500 dark:text-warm-gray-400">
                Showing exact match for <code class="font-mono">{{ activeQuestionId }}</code> (mode / subtopic ignored).
            </p>
            <p v-else-if="activeSubtopicId && mode === 'downvoted'" class="text-xs text-warm-gray-500 dark:text-warm-gray-400">
                Subtopic filter uses questionId prefix; Downvoted is filtered client-side. Count shows all docs for this subtopic (not only downvoted).
            </p>
            <p v-else-if="activeSubtopicId" class="text-xs text-warm-gray-500 dark:text-warm-gray-400">
                Ordered by questionId for subtopic <code class="font-mono">{{ activeSubtopicId }}</code>.
            </p>
        </div>

        <div v-if="!isRetrieving && explanations.length === 0" class="w-3/4 text-center text-warm-gray-500 dark:text-warm-gray-400 py-8">
            No explanations found for the current filters.
        </div>

        <div v-for="explanation in explanations" :key="explanation.id" class="rounded-lg bg-warm-gray-300 dark:bg-warm-gray-600 w-3/4 p-4">

            <div class=" flex justify-between">
                <div class="">
                    <p>Subtopic ID: <span class=" text-warm-gray-500 dark:text-warm-gray-400">{{ explanation.data.questionId.split('_')[0] }}</span></p>
                    <p>Question #: <span class="text-warm-gray-500 dark:text-warm-gray-400">{{ explanation.data.questionId.split('_')[1] }}</span></p>
                    <p class="text-xs text-warm-gray-500 dark:text-warm-gray-400 mt-1 font-mono">{{ explanation.data.questionId }}</p>
                </div>
                
                <div class="flex justify-start text-warm-gray-600 dark:text-warm-gray-300">
                    <div class="flex justify-start mr-2">
                        <HandThumbDownIcon class=" w-5 h-5 mr-0.5"/>
                        <span>{{ explanation.data.downvotes }}</span>
                    </div>
                    <div class="flex justify-start">
                        <HandThumbUpIcon class=" w-5 h-5 mr-0.5"/>
                        <span>{{ explanation.data.upvotes }}</span>
                    </div>
                </div>
            </div>

            <div class="mt-4 rounded border border-warm-gray-500 px-2 py-4 bg-warm-gray-400 dark:bg-warm-gray-700 text-warm-gray-700 dark:text-warm-gray-300">
                <div
                    :ref="(el) => setBodyRef(explanation.id, el)"
                    class="explanation-body"
                    :class="expanded[explanation.id] ? 'is-expanded' : 'is-clamped'"
                >
                    <template v-if="explanation.data.textHtml">
                        <div v-html="explanation.data.textHtml"></div>
                    </template>
                    <template v-else>
                        <p v-for="parag,i in formatExplanationForViewing(explanation.data.text)" :key="`answer-explanation-${i}`">
                            {{ parag }}
                        </p>
                    </template>
                </div>
                <button
                    v-if="overflowing[explanation.id] || expanded[explanation.id]"
                    type="button"
                    class="mt-2 text-xs text-warm-gray-600 dark:text-warm-gray-300 underline"
                    @click="toggleExpanded(explanation.id)"
                >
                    {{ expanded[explanation.id] ? 'Show less' : 'Expand' }}
                </button>
            </div>

            <div class=" flex justify-between mt-5">
                <div class="flex justify-start">
                    <button @click="openQuestion(explanation.data.questionId.split('_')[0], explanation.data.questionId.split('_')[1])" class="border rounded border-warm-gray-600 text-warm-gray-600 dark:border-warm-gray-400 dark:text-warm-gray-300 p-2 flex justify-center items-center">
                        <DocumentTextIcon class="w-4 h-4"/>
                        <span>View Question</span>
                    </button>
                </div>
                
                <div class="flex justify-end">
                    <button @click="delExplanation(explanation.id)" class="border rounded border-red-600 text-red-500 p-2 mr-2 flex justify-center items-center">
                        <TrashIcon class="w-4 h-4"/>
                        <span>Remove</span>
                    </button>
                    <button @click="openUpdateExplanationModal(explanation)" class="border rounded border-yellow-700 text-yellow-600 p-2 flex justify-center items-center">
                        <PencilSquareIcon class="w-4 h-4"/>
                        <span>Update</span>
                    </button>
                </div>

            </div>

        </div>

        <div v-if="!isLastPage" class=" w-full flex justify-center items-center">
            <button @click="getAllExplanation(true)" class="rounded border border-warm-gray-700 text-warm-gray-700 dark:border-warm-gray-400 dark:text-warm-gray-400 py-2 px-4 flex justify-center items-center disabled:opacity-40" :disabled="isRetrieving">
                <SpinningIcon v-if="isRetrieving" class="w-4 h-4 mr-1"/>
                Load more..
            </button>
        </div>

        <!-- View Question Modal -->
        <TransitionRoot as="template" id="test1" :show="isQuestionModalOpen">
            <Dialog as="div" auto-reopen="true" class="fixed z-10 inset-0 overflow-y-auto" @close="setIsQuestionModalOpen(false)">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 dark:bg-warm-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
                </TransitionChild>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>
                
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block align-bottom dark:bg-warm-gray-800  bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                    <!-- Close Button -->
                    <div class="block absolute top-0 right-0 pt-4 pr-4">
                        <button type="button" class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" @click="setIsQuestionModalOpen(false)">
                        <span class="sr-only">Close</span>
                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>
                    
                    
                    <div v-if="openedQuestion" class="relative px-1 md:px-2 max-w-4xl mx-auto py-4 mt-3">

                        <h2 class="mb-1 tracking-wider text-warm-gray-700 dark:text-warm-gray-100">Question</h2>
                        
                        <div class=" rounded border border-dashed border-warm-gray-700 dark:border-warm-gray-500 py-4 px-2">
                            <p class=" text-warm-gray-500 dark:text-warm-gray-400">{{ openedQuestion.question }}</p>
                        </div>
                        
                        <h2 class="mt-10 mb-2 tracking-wider text-warm-gray-700 dark:text-warm-gray-100">Choices:</h2>
                        <ul>
                            <li class=" p-2 rounded mb-2" :class="choice.id == openedQuestion.answer ? 'bg-gray-300 text-warm-gray-600 border-warm-gray-400 dark:bg-gray-800 dark:text-warm-gray-300 dark:border-warm-gray-700 border' : 'bg-warm-gray-300 text-warm-gray-500 dark:bg-warm-gray-700 dark:text-warm-gray-400'" v-for="choice, i in openedQuestion.choices" :key="i">{{ choice.choice }}</li>
                        </ul>
                        
                    </div>
                    </div>
                </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Update Explanation Modal -->
        <TransitionRoot as="template" id="test1" :show="isUpdateExplanationModalOpen">
            <Dialog as="div" auto-reopen="true" class="fixed z-10 inset-0 overflow-y-auto" @close="setIsUpdateExplanationModalOpen(false)">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 dark:bg-warm-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
                </TransitionChild>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>
                
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block align-bottom dark:bg-warm-gray-700  bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                        <h2 class=" text-semibold text-xl text-warm-gray-700 dark:text-warm-gray-200">Update Explanation</h2>
                        
                        <!-- Close Button -->
                        <div class="block absolute top-0 right-0 pt-4 pr-4">
                            <button type="button" class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" @click="setIsUpdateExplanationModalOpen(false)">
                            <span class="sr-only">Close</span>
                            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        
                        
                        <div class="relative max-w-4xl mx-auto py-4">

                             <!-- WYSIWYG Editor -->
                            <div class="bg-white overflow-auto mt-2 rounded">
                                <div id="my-toolbar" class="bg-warm-gray-50">
                                    <span class="ql-formats">
                                    <button class="ql-bold"></button>
                                    <button class="ql-italic"></button>
                                    <button class="ql-underline"></button>
                                    <button class="ql-strike"></button>
                                    <select class="ql-align"></select>
                                    <button class="ql-image"></button>
                                    </span>
                                </div>
                                <div class=" h-56 text-warm-gray-900">
                                    <QuillEditor v-model:content="updateExplanationForm.data.textHtml" :modules="modules" content="html" content-type="html" theme="snow" toolbar="#my-toolbar" class="focus:outline-none focus:ring-0" placeholder="Enter explanation here..." />

                                </div>
                            </div>
                            
                            <div class="flex justify-between items-center mt-5">

                                <div class="flex justify-start text-warm-gray-500">
                                    <div class="flex justify-start items-center mr-2 text-green-600">
                                        <HandThumbDownIcon class=" w-5 h-5 mr-0.5"/>
                                        <span>{{ updateExplanationForm.data.downvotes }}</span>
                                    </div>

                                    <div class="flex justify-start items-center text-red-600">
                                        <HandThumbUpIcon class=" w-5 h-5 mr-0.5"/>
                                        <span>{{ updateExplanationForm.data.upvotes }}</span>
                                    </div>
                                </div>

                                <div class="flex justify-end items-center space-x-2">
                                    <button @click="resetVoteCounter" class=" rounded p-2 text-sm border border-warm-gray-400 text-warm-gray-400">
                                        Reset vote counter
                                    </button>
                                    <button @click="saveUpdate" class=" rounded p-2 text-sm border border-yellow-600 text-yellow-600">
                                        Update
                                    </button>
                                </div>

                            </div>
                            
                        </div>
                    </div>
                </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

    </div>

    <ProcessingModal v-if="isProcessing" />

</template>

<script setup>

import {ref, computed, onMounted, nextTick, watch} from 'vue'
import {fetchExplanations, countExplanations, getSubtopicQuestions, deleteExplanation, updateExplanation} from '../../firebase/controllers/reviewerController';
import { HandThumbDownIcon, HandThumbUpIcon, DocumentTextIcon, TrashIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import ProcessingModal from '@/components/ProcessingModal';
import SpinningIcon from '@/components/SpinningIcon';

// QuillEditor
import { QuillEditor } from '@vueup/vue-quill';
import BlotFormatter from 'quill-blot-formatter';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
  } from "@headlessui/vue";

const isQuestionModalOpen = ref(false)
const setIsQuestionModalOpen = (value) => {
    isQuestionModalOpen.value = value;
}

const isUpdateExplanationModalOpen = ref(false)
const setIsUpdateExplanationModalOpen = (value) => {
    isUpdateExplanationModalOpen.value = value;
}

const isProcessing = ref(false)
const isRetrieving = ref(false);
const isCounting = ref(false)
const explanations = ref([])
const subtopicQuestions = ref({})
const openedQuestion = ref(null)
const updateExplanationForm = ref(null)

const mode = ref('downvoted') // 'downvoted' | 'all'
const searchQuestionIdInput = ref('')
const activeQuestionId = ref('')
const subtopicIdInput = ref('')
const activeSubtopicId = ref('')
const totalCount = ref(null)
const countApproximate = ref(false)

const pageSize = ref(100)
const lastDoc = ref(null)
const isLastPage = ref(false)

const expanded = ref({})
const overflowing = ref({})
const bodyRefs = {}

const setBodyRef = (id, el) => {
    if (el) bodyRefs[id] = el
    else delete bodyRefs[id]
}

const measureOverflow = () => {
    const next = { ...overflowing.value }
    Object.keys(bodyRefs).forEach((id) => {
        const el = bodyRefs[id]
        if (!el) return
        // Compare full scroll height against clamped client height
        next[id] = el.scrollHeight > el.clientHeight + 1
    })
    overflowing.value = next
}

const toggleExpanded = (id) => {
    expanded.value = { ...expanded.value, [id]: !expanded.value[id] }
    nextTick(() => measureOverflow())
}

const countLabel = computed(() => {
    if (totalCount.value == null) return ''
    const n = totalCount.value
    if (countApproximate.value) return `~${n} in subtopic (incl. non-downvoted)`
    if (activeQuestionId.value) return `${n} match${n === 1 ? '' : 'es'}`
    return `${n} total`
})

const listQueryOpts = () => ({
    mode: mode.value,
    subtopicId: activeQuestionId.value ? null : (activeSubtopicId.value || null),
    questionId: activeQuestionId.value || null,
})

const refreshCount = async () => {
    isCounting.value = true
    const res = await countExplanations(listQueryOpts())
    totalCount.value = res.isError ? null : res.count
    countApproximate.value = !!res.approximate
    isCounting.value = false
}

const getAllExplanation = async (isFromLoadMore = false) => {
    isRetrieving.value = true;

    if (!isFromLoadMore) {
        lastDoc.value = null;
        explanations.value = [];
        expanded.value = {}
        overflowing.value = {}
        await refreshCount()
    }

    const res = await fetchExplanations({
        pageSize: pageSize.value,
        lastDoc: isFromLoadMore ? lastDoc.value : null,
        ...listQueryOpts(),
    });

    explanations.value.push(...res.explanations);
    lastDoc.value = res.lastDoc;
    isLastPage.value = res.isLastPage;
    isRetrieving.value = false;
    await nextTick()
    measureOverflow()
}

const onModeChange = async () => {
    if (activeQuestionId.value || isRetrieving.value) return
    await getAllExplanation(false)
}

const applySearch = async () => {
    const qid = searchQuestionIdInput.value.trim()
    activeQuestionId.value = qid
    await getAllExplanation(false)
}

const clearSearch = async () => {
    searchQuestionIdInput.value = ''
    activeQuestionId.value = ''
    await getAllExplanation(false)
}

const applySubtopicFilter = async () => {
    activeSubtopicId.value = subtopicIdInput.value.trim()
    await getAllExplanation(false)
}

const clearSubtopicFilter = async () => {
    subtopicIdInput.value = ''
    activeSubtopicId.value = ''
    await getAllExplanation(false)
}

const openQuestion = async (subtopicId, qId) => {

    await getQuestion(subtopicId, qId);
    setIsQuestionModalOpen(true);

}

const getQuestion = async (subtopicId, qId) => {

    // check if the subtopic already fetch previously from database
    openedQuestion.value = subtopicQuestions.value[subtopicId] ? subtopicQuestions.value[subtopicId].filter((question) => question.id == qId)[0] : null;
    // if already have and found then return
    if (openedQuestion.value != null) {
        return;
    }

    // if not yet fetch then fetch from database
    subtopicQuestions.value[subtopicId] = await getSubtopicQuestions(subtopicId);
    openedQuestion.value = subtopicQuestions.value[subtopicId].filter((question) => question.id == qId)[0]
}

const delExplanation = async (explanationId) => {
    isProcessing.value = true;
    await deleteExplanation(explanationId);

    await getAllExplanation();
    
    isProcessing.value = false;
}

const openUpdateExplanationModal = (explanation) => {

    updateExplanationForm.value = JSON.parse(JSON.stringify(explanation));

    // check if the explanation only have the text and no textHtml (meaning explanation was fetch from chatGPT)
    // convert raw data text to WYSIWYG compatible
    let toWYSIWYG = '';
    if (updateExplanationForm.value.data.text && !updateExplanationForm.value.data.textHtml) {
        let processedExplanation = null;
        processedExplanation = updateExplanationForm.value.data.text.replace(/(?:\r\n|\r|\n)/g, '\n\n');
        let prags = processedExplanation.split(/(?:\r\n|\r|\n)/g).filter(item => item !== '');
        prags.forEach(prag => {
            toWYSIWYG = toWYSIWYG + `<p>${prag}<p/>`
        })
    }
    updateExplanationForm.value.data.textHtml = updateExplanationForm.value.data.textHtml ? updateExplanationForm.value.data.textHtml.trim() : updateExplanationForm.value.data.text ? toWYSIWYG : '';


    setIsUpdateExplanationModalOpen(true);
}

const resetVoteCounter = () => {
    updateExplanationForm.value.data.upvotes = 0;
    updateExplanationForm.value.data.downvotes = 0;
}

const saveUpdate = async () => {
    isProcessing.value = true;
    await updateExplanation(updateExplanationForm.value);
    await getAllExplanation();

    setIsUpdateExplanationModalOpen(false);
    isProcessing.value = false;
}

// Part or needed in QuillEditor
const modules = {
    name: 'blotFormatter',  
    module: BlotFormatter, 
    options: {/* options */},
}

onMounted(async ()=> {
    await getAllExplanation(false);
})

// Re-measure after list changes (e.g. images in HTML settling)
watch(explanations, async () => {
    await nextTick()
    // Second tick helps after v-html paints
    requestAnimationFrame(() => measureOverflow())
})

const formatExplanationForViewing = (rawExplanation) => {
    let processedExplanation = null;
    processedExplanation = rawExplanation.replace(/(?:\r\n|\r|\n)/g, '\n\n');
    return processedExplanation.split(/(?:\r\n|\r|\n)/g).filter(item => item !== '');
}

</script>

<style scoped>
.explanation-body.is-clamped {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
}
.explanation-body.is-expanded {
    overflow: visible;
}

/* One chevron only: hide native arrow; @tailwindcss/forms (or this) draws a single icon */
.explanation-mode-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
    background-position: right 0.35rem center;
    background-repeat: no-repeat;
    background-size: 1.1em 1.1em;
}
.explanation-mode-select::-ms-expand {
    display: none;
}
</style>
